import {
  buildScoutPrivateDocumentExtractionWatchPartUrl,
  parseScoutPrivateDocumentIdFromPartUrl,
} from "encamp-constants/scout";
import {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";

import {
  getScoutMessageText,
  type ScoutFilePart,
  type ScoutChatMessage,
} from "./Chat/message/scoutChatMessage";
import {
  isScoutPrivateDocumentExtractionWatchPartUrl,
  parseScoutExtractionWatchDocumentIdsFromText,
} from "./Chat/scoutExtractionWatchMessage";
import {
  mapScoutAttachmentToPreview,
  type ScoutAttachment,
  type ScoutOptimisticAttachmentPreview,
} from "./scoutSubmitAttachments";

type ScoutRetryPayload = {
  text: string;
  attachments: ScoutAttachment[];
  attachmentPreviews: ScoutOptimisticAttachmentPreview[];
  extractionWatchPrivateDocumentIds: string[];
};

function isAbortUserMessage(message: ScoutChatMessage): boolean {
  return message.parts.some((part) => part.type === "abort");
}

function resolvePrivateDocumentIdFromFilePart(part: ScoutFilePart): string | undefined {
  if (part.document?.id) {
    return part.document.id;
  }

  return parseScoutPrivateDocumentIdFromPartUrl(part.url);
}

export function mapScoutFilePartToAttachment(part: ScoutFilePart): ScoutAttachment | undefined {
  if (isScoutPrivateDocumentExtractionWatchPartUrl(part.url)) {
    return undefined;
  }

  const privateDocumentId = resolvePrivateDocumentIdFromFilePart(part);
  if (!privateDocumentId) {
    return undefined;
  }

  const filename = part.filename ?? part.title ?? "Attachment";

  return {
    privateDocumentId,
    filename,
    title: part.title ?? filename,
    mediaType: part.mediaType ?? "application/octet-stream",
    sizeBytes: part.document?.sizeBytes ?? 0,
    signedDownloadUrl: part.document?.signedDownloadUrl ?? undefined,
    thumbnailSignedUrl: part.document?.thumbnailSignedUrl ?? undefined,
    pageCount: part.document?.pageCount,
    ingestionMode: part.ingestionMode as ScoutPrivateDocumentIngestionMode | undefined,
    extractionStatus: part.extractionStatus as ScoutPrivateDocumentExtractionStatus | undefined,
  };
}

export function mapScoutFilePartToRetryPreview(
  part: ScoutFilePart
): ScoutOptimisticAttachmentPreview | undefined {
  const privateDocumentId = resolvePrivateDocumentIdFromFilePart(part);
  if (!privateDocumentId) {
    return undefined;
  }

  if (isScoutPrivateDocumentExtractionWatchPartUrl(part.url)) {
    const filename = part.filename ?? part.title ?? "Document.pdf";
    return {
      privateDocumentId,
      filename,
      mediaType: part.mediaType ?? "application/pdf",
      sizeBytes: part.document?.sizeBytes ?? 0,
      signedDownloadUrl: part.document?.signedDownloadUrl ?? undefined,
      thumbnailSignedUrl: part.document?.thumbnailSignedUrl ?? undefined,
      pageCount: part.document?.pageCount,
      ingestionMode:
        (part.ingestionMode as ScoutPrivateDocumentIngestionMode | undefined) ??
        ScoutPrivateDocumentIngestionMode.LargeFileExtraction,
      extractionStatus:
        (part.extractionStatus as ScoutPrivateDocumentExtractionStatus | undefined) ??
        ScoutPrivateDocumentExtractionStatus.Pending,
      filePartUrl: buildScoutPrivateDocumentExtractionWatchPartUrl(privateDocumentId),
    };
  }

  const attachment = mapScoutFilePartToAttachment(part);
  if (!attachment) {
    return undefined;
  }

  return mapScoutAttachmentToPreview(attachment);
}

function resolveExtractionWatchDocumentIds(message: ScoutChatMessage): string[] {
  const documentIds = new Set<string>();

  for (const part of message.parts) {
    if (part.type === "file" && isScoutPrivateDocumentExtractionWatchPartUrl(part.url)) {
      const documentId = resolvePrivateDocumentIdFromFilePart(part);
      if (documentId) {
        documentIds.add(documentId);
      }
    }
  }

  for (const part of message.parts) {
    if (part.type === "text") {
      for (const documentId of parseScoutExtractionWatchDocumentIdsFromText(part.text)) {
        documentIds.add(documentId);
      }
    }
  }

  return [...documentIds];
}

/**
 * Rebuilds the send payload for Retry from the latest user message in history.
 * Retry has no composer attachment state, so text, mutation ids, optimistic
 * previews, and extraction-watch ids are derived from persisted message parts.
 */
export function getLastUserTurnForRetry(
  messages: ScoutChatMessage[]
): ScoutRetryPayload | undefined {
  let lastUserMessage: ScoutChatMessage | undefined;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "user" || isAbortUserMessage(message)) {
      continue;
    }

    lastUserMessage = message;
    break;
  }

  if (!lastUserMessage) {
    return undefined;
  }

  const text = getScoutMessageText(lastUserMessage).trim();
  const fileParts = lastUserMessage.parts.filter(
    (part): part is ScoutFilePart => part.type === "file"
  );
  const attachments = fileParts
    .map((part) => mapScoutFilePartToAttachment(part))
    .filter((attachment): attachment is ScoutAttachment => attachment !== undefined);
  const attachmentPreviews = fileParts
    .map((part) => mapScoutFilePartToRetryPreview(part))
    .filter((preview): preview is ScoutOptimisticAttachmentPreview => preview !== undefined);
  const extractionWatchPrivateDocumentIds = resolveExtractionWatchDocumentIds(lastUserMessage);

  if (!text && attachments.length === 0 && extractionWatchPrivateDocumentIds.length === 0) {
    return undefined;
  }

  return {
    text,
    attachments,
    attachmentPreviews,
    extractionWatchPrivateDocumentIds,
  };
}

export function canRetryLastUserTurn(payload: ScoutRetryPayload | undefined): boolean {
  if (!payload) {
    return false;
  }

  return payload.text.length > 0 || payload.attachments.length > 0;
}
